/** Client enums. These are the vocabulary the DB, API and UI all agree on. */

export const CLIENT_STATUSES = [
  'pending_review',
  'confirmed',
  'active',
  'processing',
  'deployed',
  'rejected',
  'inactive',
  'blacklisted',
] as const
export type ClientStatus = (typeof CLIENT_STATUSES)[number]

export const CLIENT_STATUS_LABELS: Record<ClientStatus, string> = {
  pending_review: 'Pending review',
  confirmed: 'Confirmed',
  active: 'Active',
  processing: 'Processing',
  deployed: 'Deployed',
  rejected: 'Rejected',
  inactive: 'Inactive',
  blacklisted: 'Blacklisted',
}

/**
 * Statuses that count as a live, contactable client. Campaign and birthday
 * sends filter to these (PLAN §7.2) — rejected/blacklisted people must never
 * receive marketing.
 */
export const CONTACTABLE_STATUSES: readonly ClientStatus[] = [
  'confirmed',
  'active',
  'processing',
  'deployed',
]

export const CLIENT_SOURCES = ['manual', 'self_registration'] as const
export type ClientSource = (typeof CLIENT_SOURCES)[number]

export const GENDERS = ['male', 'female'] as const
export type Gender = (typeof GENDERS)[number]

export const MARITAL_STATUSES = ['single', 'married', 'divorced', 'widowed'] as const
export type MaritalStatus = (typeof MARITAL_STATUSES)[number]

export const PASSPORT_STATUSES = ['none', 'applied', 'in_hand'] as const
export type PassportStatus = (typeof PASSPORT_STATUSES)[number]

export const PASSPORT_STATUS_LABELS: Record<PassportStatus, string> = {
  none: 'No passport',
  applied: 'Applied',
  in_hand: 'In hand',
}

export const EDUCATION_LEVELS = [
  'none',
  'below_ol',
  'ol',
  'al',
  'vocational',
  'diploma',
  'degree',
  'postgraduate',
] as const
export type EducationLevel = (typeof EDUCATION_LEVELS)[number]

export const EDUCATION_LEVEL_LABELS: Record<EducationLevel, string> = {
  none: 'No formal education',
  below_ol: 'Below O/L',
  ol: 'O/L',
  al: 'A/L',
  vocational: 'Vocational / NVQ',
  diploma: 'Diploma',
  degree: 'Degree',
  postgraduate: 'Postgraduate',
}

export const LANGUAGE_PROFICIENCIES = ['basic', 'intermediate', 'fluent', 'native'] as const
export type LanguageProficiency = (typeof LANGUAGE_PROFICIENCIES)[number]

export const LANGUAGES = [
  'sinhala',
  'tamil',
  'english',
  'arabic',
  'korean',
  'japanese',
  'hindi',
  'malay',
  'chinese',
  'russian',
  'romanian',
] as const
export type Language = (typeof LANGUAGES)[number]

/** Currencies an expected salary is likely quoted in. */
export const CURRENCIES = [
  'LKR',
  'USD',
  'EUR',
  'GBP',
  'SAR',
  'AED',
  'QAR',
  'KWD',
  'OMR',
  'BHD',
  'ILS',
  'SGD',
  'MYR',
  'KRW',
  'JPY',
  'RON',
  'PLN',
  'HUF',
] as const
export type Currency = (typeof CURRENCIES)[number]

export const CONSENT_CHANNELS = ['sms', 'whatsapp', 'email'] as const
export type ConsentChannel = (typeof CONSENT_CHANNELS)[number]
